/**
 * useUnifiedChartData Hook
 *
 * React hook that fetches and normalizes all data sources
 * for the unified event chart.
 */

import { useMemo } from 'react';
import {
  normalizeChartData,
  downsampleChartData,
  NormalizedDataPoint,
  ChartDataResult,
  RawOddsPoint,
  RawPolymarketPoint,
  RawSignalPoint
} from '../utils/chart-data-normalizer';

// ============================================
// Types
// ============================================

export interface EventData {
  id: string;
  homeTeam?: string;
  awayTeam?: string;
  commenceTime?: string;
  sport?: string;
}

export interface OddsHistoryPoint {
  timestamp: string;
  bookmaker: string;
  outcomeName: string;
  price: number;
  isSharp: boolean | null;
}

export interface PolymarketHistoryPoint {
  timestamp: string;
  outcome: string;
  midPrice: number;
  bidPrice?: number;
  askPrice?: number;
}

export interface SignalHistoryPoint {
  timestamp: string;
  chainValue: number;
  direction: 'home' | 'away' | 'over' | 'under';
  signalType?: string;
}

export interface UseUnifiedChartDataProps {
  event: EventData | null;
  oddsHistory: OddsHistoryPoint[];
  polymarketHistory: PolymarketHistoryPoint[];
  signals?: SignalHistoryPoint[];
  maxPoints?: number;
  enabled?: boolean;
}

export interface UseUnifiedChartDataResult {
  chartData: NormalizedDataPoint[];
  meta: ChartDataResult['meta'] | null;
  isLoading: boolean;
  hasData: boolean;
  hasOddsData: boolean;
  hasPolyData: boolean;
  hasSignals: boolean;
  priceGapRange: { min: number; max: number };
  probabilityRange: { min: number; max: number };
}

// ============================================
// Helper Functions
// ============================================

function calculateRange(
  data: NormalizedDataPoint[],
  accessor: (d: NormalizedDataPoint) => number | null
): { min: number; max: number } {
  const values = data
    .map(accessor)
    .filter((v): v is number => v !== null);

  if (values.length === 0) {
    return { min: 0, max: 100 };
  }

  const min = Math.min(...values);
  const max = Math.max(...values);

  // Add 10% padding
  const padding = (max - min) * 0.1 || 5;
  return {
    min: Math.max(0, min - padding),
    max: Math.min(100, max + padding)
  };
}

function calculateGapRange(data: NormalizedDataPoint[]): { min: number; max: number } {
  const homeGaps = data.map(d => d.gapHome).filter((v): v is number => v !== null);
  const awayGaps = data.map(d => d.gapAway).filter((v): v is number => v !== null);
  const allGaps = [...homeGaps, ...awayGaps];

  if (allGaps.length === 0) {
    return { min: -10, max: 10 };
  }

  const min = Math.min(...allGaps);
  const max = Math.max(...allGaps);

  // Symmetric around zero for better visualization
  const absMax = Math.max(Math.abs(min), Math.abs(max));
  const padding = absMax * 0.1 || 2;
  return {
    min: -absMax - padding,
    max: absMax + padding
  };
}

// ============================================
// Main Hook
// ============================================

export function useUnifiedChartData({
  event,
  oddsHistory,
  polymarketHistory,
  signals = [],
  maxPoints = 200,
  enabled = true
}: UseUnifiedChartDataProps): UseUnifiedChartDataResult {
  // Memoize the normalization to avoid recalculating on every render
  const result = useMemo(() => {
    if (!enabled || !event) {
      return {
        chartData: [],
        meta: null,
        isLoading: false,
        hasData: false,
        hasOddsData: false,
        hasPolyData: false,
        hasSignals: false,
        priceGapRange: { min: -10, max: 10 },
        probabilityRange: { min: 0, max: 100 }
      };
    }

    // Convert to raw format expected by normalizer
    const rawOdds: RawOddsPoint[] = oddsHistory.map(o => ({
      timestamp: o.timestamp,
      bookmaker: o.bookmaker,
      outcomeName: o.outcomeName,
      price: o.price,
      isSharp: o.isSharp
    }));

    const rawPoly: RawPolymarketPoint[] = polymarketHistory.map(p => ({
      timestamp: p.timestamp,
      outcome: p.outcome,
      midPrice: p.midPrice,
      bidPrice: p.bidPrice,
      askPrice: p.askPrice
    }));

    const rawSignals: RawSignalPoint[] = signals.map(s => ({
      timestamp: s.timestamp,
      chainValue: s.chainValue,
      direction: s.direction,
      signalType: s.signalType
    }));

    // Normalize all data
    const normalized = normalizeChartData(rawOdds, rawPoly, rawSignals, {
      homeTeam: event.homeTeam,
      awayTeam: event.awayTeam
    });

    // Downsample if needed
    const downsampled = downsampleChartData(normalized.data, maxPoints);

    // Calculate ranges for chart axes
    const priceGapRange = calculateGapRange(downsampled);
    const probabilityRange = calculateRange(downsampled, d =>
      d.oddsHome !== null ? d.oddsHome :
      d.polyHome !== null ? d.polyHome : null
    );

    return {
      chartData: downsampled,
      meta: normalized.meta,
      isLoading: false,
      hasData: downsampled.length > 0,
      hasOddsData: oddsHistory.length > 0,
      hasPolyData: polymarketHistory.length > 0,
      hasSignals: signals.length > 0,
      priceGapRange,
      probabilityRange
    };
  }, [event, oddsHistory, polymarketHistory, signals, maxPoints, enabled]);

  return result;
}

// ============================================
// Utility Hooks
// ============================================

/**
 * Hook to get chart config based on data availability
 */
export function useChartConfig(result: UseUnifiedChartDataResult) {
  return useMemo(() => {
    const layers: Array<'odds' | 'polymarket' | 'gap' | 'signals'> = [];

    if (result.hasOddsData) layers.push('odds');
    if (result.hasPolyData) layers.push('polymarket');
    if (result.hasOddsData && result.hasPolyData) layers.push('gap');
    if (result.hasSignals) layers.push('signals');

    return {
      layers,
      showOdds: result.hasOddsData,
      showPolymarket: result.hasPolyData,
      showGap: result.hasOddsData && result.hasPolyData,
      showSignals: result.hasSignals,
      yAxisDomain: result.probabilityRange,
      gapAxisDomain: result.priceGapRange
    };
  }, [result]);
}

export default useUnifiedChartData;
